/**
 * BlocIdentité (zone [1]) — médaillon 48px + titre + sous-titre + étoiles.
 *
 * Sous-titre `sublabel_mono` : la référence (avant « · ») est rendue en mono,
 * le qualificatif en normal — reproduit fidèlement la maquette (« CLI-0000004 »
 * mono · « Entreprise »).
 */

import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Medallion } from '../primitives'
import type { SummaryHeader } from '../types'

function Stars({ n }: { n: number }) {
    return (
        <span className="inline-flex gap-px align-middle" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((k) => (
                <Star
                    key={k}
                    className={cn(
                        'h-3 w-3',
                        k < n ? 'fill-warning text-warning' : 'text-muted-foreground/40',
                    )}
                />
            ))}
        </span>
    )
}

function Sublabel({ text, mono }: { text: string; mono: boolean }) {
    if (!mono) return <>{text}</>
    const sep = ' · '
    const idx = text.indexOf(sep)
    if (idx === -1) return <span className="font-mono text-[11px]">{text}</span>
    return (
        <>
            <span className="font-mono text-[11px]">{text.slice(0, idx)}</span>
            {text.slice(idx)}
        </>
    )
}

export function SummaryHeaderBlock({ header }: { header: SummaryHeader }) {
    return (
        <div className="flex items-start gap-3.5">
            <Medallion header={header} />
            <div className="min-w-0 pt-0.5">
                <div className="line-clamp-2 text-base font-semibold text-foreground">
                    {header.label}
                </div>
                {(header.sublabel || header.stars) && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        {header.sublabel && (
                            <span className="truncate">
                                <Sublabel text={header.sublabel} mono={header.sublabel_mono} />
                            </span>
                        )}
                        {!!header.stars && <Stars n={header.stars} />}
                    </div>
                )}
            </div>
        </div>
    )
}
